import { useTenantConfig } from '@webmono/storefront-ui/providers'
import { formatCents, cn } from '@webmono/storefront-ui/utils'
import { useAnalytics } from '@/lib/hooks'

function StatCard({
  label,
  value,
  subValue,
  trend,
  icon,
}: {
  label: string
  value: string
  subValue?: string
  trend?: { value: number; label: string }
  icon: React.ReactNode
}) {
  return (
    <div className="bg-card rounded-lg border border-border p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          {icon}
        </div>
        {trend && (
          <span className={cn(
            'text-xs px-2 py-0.5 rounded-full font-medium',
            trend.value >= 0 ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive'
          )}>
            {trend.value >= 0 ? '+' : ''}{trend.value}% {trend.label}
          </span>
        )}
      </div>
      <p className="text-2xl font-heading font-semibold text-foreground">{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
      {subValue && <p className="text-xs text-muted-foreground mt-1">{subValue}</p>}
    </div>
  )
}

export function MerchantAnalyticsPage() {
  const tenant = useTenantConfig()
  const { data: analytics, isLoading } = useAnalytics()

  if (isLoading || !analytics) {
    return (
      <div>
        <h1 className="text-2xl font-heading font-semibold text-foreground mb-6">Analytics</h1>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-card rounded-lg border border-border p-4 animate-pulse">
              <div className="w-10 h-10 bg-muted rounded-full mb-3" />
              <div className="h-8 bg-muted rounded w-1/2 mb-2" />
              <div className="h-4 bg-muted rounded w-2/3" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  const revenueGrowth = analytics.comparisonPeriod.grossSalesCents > 0
    ? Math.round(((analytics.grossSalesCents - analytics.comparisonPeriod.grossSalesCents) / analytics.comparisonPeriod.grossSalesCents) * 100)
    : 0

  const orderGrowth = analytics.comparisonPeriod.orderCount > 0
    ? Math.round(((analytics.orderCount - analytics.comparisonPeriod.orderCount) / analytics.comparisonPeriod.orderCount) * 100)
    : 0

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-heading font-semibold text-foreground">Analytics</h1>
        <span className="text-sm text-muted-foreground">Today</span>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-8">
        <StatCard
          label="Gross Sales"
          value={formatCents(analytics.grossSalesCents, tenant.operations.currency)}
          trend={{ value: revenueGrowth, label: 'vs last week' }}
          icon={
            <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
        <StatCard
          label="Net Sales"
          value={formatCents(analytics.netSalesCents, tenant.operations.currency)}
          subValue="After fees and refunds"
          icon={
            <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          }
        />
        <StatCard
          label="Orders"
          value={analytics.orderCount.toString()}
          trend={{ value: orderGrowth, label: 'vs last week' }}
          icon={
            <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          }
        />
        <StatCard
          label="Average Order"
          value={formatCents(analytics.averageOrderValueCents, tenant.operations.currency)}
          icon={
            <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          }
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="bg-card rounded-lg border border-border p-4">
          <h2 className="font-heading font-semibold text-foreground mb-4">Top Selling Items</h2>
          <div className="space-y-3">
            {analytics.topItems.map((item, index) => (
              <div key={item.itemId} className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">
                  {index + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground truncate">{item.name}</p>
                  <p className="text-xs text-muted-foreground">{item.quantity} sold</p>
                </div>
                <span className="font-mono text-sm text-foreground">
                  {formatCents(item.revenueCents, tenant.operations.currency)}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-4">
          <h2 className="font-heading font-semibold text-foreground mb-4">Orders by Hour</h2>
          <div className="flex items-end gap-1 h-40">
            {analytics.hourlyBreakdown.map((hour) => {
              const maxOrders = Math.max(...analytics.hourlyBreakdown.map((h) => h.orderCount))
              const height = maxOrders > 0 ? (hour.orderCount / maxOrders) * 100 : 0

              return (
                <div key={hour.hour} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full flex flex-col items-center justify-end" style={{ height: '140px' }}>
                    <div
                      className="w-full bg-primary/80 rounded-t transition-all"
                      style={{ height: `${height}%`, minHeight: hour.orderCount > 0 ? '4px' : '0' }}
                    />
                  </div>
                  <span className="text-xs text-muted-foreground">{hour.hour}</span>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
